import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../../redux/store";
import {
  upsertTrainerTemplate,
  deleteTrainerTemplate,
  updateTrainerCustomExercises,
  upsertTrainerMealPlans,
  deleteTrainerMealPlan,
} from "../../../../../redux/thunks/thunk-partners";

import { fetchRecipesAndMealPlans } from "../../../../../redux/thunks/thunk-recipes";

import {
  Exercise,
  TemplateType,
  User,
  Recipe,
  MealPlans as MealPlanType,
} from "../../../../../models/types";

import React, { useState, useEffect, useCallback } from "react";

import GlassContainer from "../../../components/liquid-glass";
import ExercisesVaultList from "./ExercisesVaultList";
import TemplatesVault from "./TemplatesVault";
import RecipesVaultList from "./RecipesVaultList";
import MealPlansVaultList from "./MealPlansVaultList";

import { Box, Stack, Typography, IconButton, Button } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

// ─────────────────────────────────────────────────────────────
// CardStack: shared lil glass card stack preview
// ─────────────────────────────────────────────────────────────
interface CardStackProps {
  count: number;
  title: string;
  number: number;
  onClick: () => void;
}

const CardStack: React.FC<CardStackProps> = ({
  count,
  title,
  number,
  onClick,
}) => {
  const [hovered, setHovered] = useState(false);
  const stackCount = Math.max(1, count);

  return (
    <Box
      sx={{
        position: "relative",
        width: 300,
        height: 200,
        cursor: "pointer",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
    >
      {[...Array(stackCount)].map((_, i) => (
        <GlassContainer
          key={i}
          blur={22}
          radius={18}
          dontHoverPop
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            transition: "transform 0.3s ease",
            transform: hovered
              ? `translate(${i * 5}px, ${i * 5}px) rotate(${i * 2}deg)`
              : `translate(${i * 1}px, ${i * 1}px) rotate(0deg)`,
            zIndex: stackCount - i,
          }}
        >
          {i === 0 && (
            <Box
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <Typography variant="h6">{title}</Typography>
              <Typography>{number} custom</Typography>
            </Box>
          )}
        </GlassContainer>
      ))}
    </Box>
  );
};

type VaultSection = "exercises" | "templates" | "recipes" | "mealPlans";

interface ExerciseBankVaultProps {
  initialSection?: VaultSection | null;
}

// ─────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────
export default function ExerciseBankVault({
  initialSection = null,
}: ExerciseBankVaultProps) {
  const dispatch = useDispatch<AppDispatch>();

  // logged-in trainer user (a Partner/User hybrid in your model)
  const partner: User | null = useSelector(
    (s: RootState) => s.partners.partnerData?.partnerData as User | null
  );

  const darkMode = useSelector((s: RootState) => !!s.partners.darkMode);

  // global recipes & mealPlans from Redux partners slice
  const recipesFromStore = useSelector(
    (s: RootState) => s.partners.recipes
  ) as Recipe[];

  const mealPlansFromStore = useSelector(
    (s: RootState) => s.partners.mealPlans
  ) as MealPlanType[];

  // fetch status (could be used for loading UI / skeletons later)
  const recipesFetchStatus = useSelector(
    (s: RootState) => s.partners.recipesFetchStatus
  );

  // token lives elsewhere in state (auth slice)
  const idToken = useSelector(
    (s: RootState) => (s as any)?.auth?.idToken as string | undefined
  );

  // ── Local editable state for exercises / templates / mealPlans.
  // We'll keep local mirrors so UI updates instantly on optimistic actions,
  // then we dispatch thunks so Redux + Dynamo stay in sync.
  const [exercises, setExercises] = useState<Exercise[]>(
    () => partner?.customExercises ?? []
  );
  const [templates, setTemplates] = useState<TemplateType[]>(
    () => partner?.templates ?? []
  );
  const [mealPlans, setMealPlans] = useState<MealPlanType[]>(
    () => mealPlansFromStore ?? []
  );

  // Sync local exercises/templates when partner changes in Redux.
  // Sync local mealPlans when the slice’s mealPlans array changes in Redux.
  // (Templates come from partner.templates, but mealPlans come from slice.mealPlans.)
  useEffect(() => {
    if (partner?.customExercises) {
      setExercises(partner.customExercises);
    }
  }, [partner?.customExercises]);

  useEffect(() => {
    if (partner?.templates) {
      setTemplates(partner.templates);
    }
  }, [partner?.templates]);

  useEffect(() => {
    if (Array.isArray(mealPlansFromStore)) {
      setMealPlans(mealPlansFromStore);
    }
  }, [mealPlansFromStore]);

  // ── Fetch recipes + mealPlans from Lambda on mount / when trainer userId becomes available
  useEffect(() => {
    if (!partner?.userId) return;
    // only fetch when first loaded or errored; otherwise we assume cache
    if (recipesFetchStatus === "idle" || recipesFetchStatus === "failed") {
      dispatch(fetchRecipesAndMealPlans());
    }
  }, [dispatch, partner?.userId, recipesFetchStatus]);

  // ─────────────────────────────────────────────────────────
  // HELPERS / PERSISTERS
  // ─────────────────────────────────────────────────────────

  // Custom Exercises persistence
  const persistExercises = useCallback(
    (next: Exercise[]) => {
      setExercises(next); // optimistic
      if (!idToken || !partner?.userId) return;
      dispatch(
        updateTrainerCustomExercises({
          idToken,
          trainerUserId: partner.userId,
          customExercises: next,
        })
      );
    },
    [dispatch, idToken, partner?.userId]
  );

  // ── EXERCISE HANDLERS
  const handleCreateExercise = (ex: Exercise) => {
    const nextCalc = (prev: Exercise[]) => {
      if (ex.id && prev.some((p) => p.id === ex.id)) {
        return prev.map((p) => (p.id === ex.id ? ex : p));
      }
      return [ex, ...prev];
    };
    setExercises((prev) => {
      const computed = nextCalc(prev);
      if (idToken && partner?.userId) {
        dispatch(
          updateTrainerCustomExercises({
            idToken,
            trainerUserId: partner.userId,
            customExercises: computed,
          })
        );
      }
      return computed;
    });
  };

  const handleUpdateExercise = (ex: Exercise) => {
    const computed = exercises.map((p) => (p.id === ex.id ? ex : p));
    persistExercises(computed);
  };

  const handleDeleteExercise = (id?: string | null) => {
    const computed = exercises.filter((p) => p.id !== id);
    persistExercises(computed);
  };

  // ── TEMPLATE HANDLERS
  const handleSaveTemplate = (t: TemplateType) => {
    // optimistic update locally
    setTemplates((prev) => {
      const idx = prev.findIndex((p) => p.id === t.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = t;
        return next;
      }
      return [t, ...prev];
    });

    // persist via thunk -> updates Dynamo + Redux.partnerData
    if (idToken && partner?.userId) {
      dispatch(
        upsertTrainerTemplate({
          idToken,
          trainerUserId: partner.userId,
          template: t,
        })
      );
    }
  };

  const handleDeleteTemplate = (id: string) => {
    // optimistic local remove
    setTemplates((prev) => prev.filter((t) => t.id !== id));

    // persist delete
    if (idToken && partner?.userId) {
      dispatch(
        deleteTrainerTemplate({
          idToken,
          trainerUserId: partner.userId,
          templateId: id,
        })
      );
    }
  };

  // ── RECIPE HANDLERS
  // (Right now recipes are sourced from Redux directly; we don't maintain a local copy here.
  //  We'll wire optimistic recipe upserts/deletes later if needed.)
  const handleCreateOrUpdateRecipe = (r: Recipe) => {
    console.log("create/update recipe requested", r);
  };

  const handleDeleteRecipe = (recipeId: string) => {
    console.log("delete recipe requested", recipeId);
  };

  // ── MEAL PLAN HANDLERS
  // Similar to templates, but uses upsertTrainerMealPlans / deleteTrainerMealPlan
  const handleCreateOrUpdateMealPlan = (plan: MealPlanType) => {
    // optimistic local merge into mealPlans[] state
    setMealPlans((prev) => {
      const idx = prev.findIndex((p) => p.id === plan.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = plan;
        return next;
      }
      return [plan, ...prev];
    });

    // persist remotely + update redux.partnerData + redux.mealPlans
    if (idToken && partner?.userId) {
      dispatch(
        upsertTrainerMealPlans({
          idToken,
          trainerUserId: partner.userId,
          mealPlan: plan,
        })
      );
    }
  };

  const handleDeleteMealPlan = (planId: string) => {
    // optimistic local remove
    setMealPlans((prev) => prev.filter((p) => p.id !== planId));

    // persist remotely + update redux.partnerData + redux.mealPlans
    if (idToken && partner?.userId) {
      dispatch(
        deleteTrainerMealPlan({
          idToken,
          trainerUserId: partner.userId,
          planId,
        })
      );
    }
  };

  // ── VIEW STATE
  const [selectedSection, setSelectedSection] = useState<VaultSection | null>(
    initialSection ?? null
  );
  // ─────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        height: "calc(100vh - 72px)",
        px: 1,
        display: "grid",
        gridTemplateRows: "auto 1fr",
        gap: 1,
      }}
    >
      {selectedSection === null ? (
        <Stack
          direction={{ xs: "column", md: "row" }}
          flexWrap="wrap"
          justifyContent="center"
          alignItems="center"
          spacing={4}
          sx={{
            height: "100%",
            rowGap: 4,
            columnGap: 4,
          }}
        >
          <CardStack
            count={exercises.length}
            title="Exercises"
            number={exercises.length}
            onClick={() => setSelectedSection("exercises")}
          />
          <CardStack
            count={templates.length}
            title="Workout Templates"
            number={templates.length}
            onClick={() => setSelectedSection("templates")}
          />
          <CardStack
            count={recipesFromStore.length}
            title="Recipes"
            number={recipesFromStore.length}
            onClick={() => setSelectedSection("recipes")}
          />
          <CardStack
            count={mealPlans.length}
            title="Meal Plans"
            number={mealPlans.length}
            onClick={() => setSelectedSection("mealPlans")}
          />
        </Stack>
      ) : selectedSection === "exercises" ? (
        <ExercisesVaultList
          exercises={exercises}
          onCreateExercise={handleCreateExercise}
          onUpdateExercise={handleUpdateExercise}
          onDeleteExercise={handleDeleteExercise}
        />
      ) : selectedSection === "templates" ? (
        <TemplatesVault
          templates={templates}
          exercises={exercises}
          onSaveTemplate={handleSaveTemplate}
          onDeleteTemplate={handleDeleteTemplate}
          onCreateExercise={handleCreateExercise}
        />
      ) : selectedSection === "recipes" ? (
        <RecipesVaultList
          recipes={recipesFromStore}
          onCreateOrUpdateRecipe={handleCreateOrUpdateRecipe}
          onDeleteRecipe={handleDeleteRecipe}
        />
      ) : (
        <MealPlansVaultList
          mealPlans={mealPlans}
          recipes={recipesFromStore}
          onCreateOrUpdateMealPlan={handleCreateOrUpdateMealPlan}
          onDeleteMealPlan={handleDeleteMealPlan}
        />
      )}
    </Box>
  );
}

// types (unchanged, just included for completeness)
export interface ClientTrainerMeetingMinimal {
  id: string;
  trainerUserId: string;
  clientUserId: string;
  scheduledStartAt: string;
  scheduledEndAt: string;
  mode?: string;
  status?: string;
  title?: string;
  notes?: string;

  createdAt: string;
  updatedAt?: string;

  pk: string;
  sk: string;
}
